import clsx from 'clsx';
import React, { useEffect, useState } from 'react';

import LazyImgixImage from '@components/LazyImgixImage';
import NewsletterActionButton from '@components/pages/NewsletterIndexLanding/NewsletterCard/NewsletterActionButton';
import SignUpModal from '@components/pages/NewsletterIndexLanding/SignUpModal';
import { useAppContext } from '@contexts/AppContext';
import useCurrentNewsletterSubscription from '@hooks/useCurrentNewsletterSubscription';
import useEventTracking from '@hooks/useEventTracking';
import useNewsletter from '@hooks/useNewsletter';
import { ClickTarget, ContentType, click } from '@tracking';
import type { User } from '@utils/backend-api/currentUser';

import type { NewsletterBlockProps } from '../interfaces';

const NewsletterBlock = ({ config, content }: NewsletterBlockProps) => {
  const newsletterId = content?.newsletter_id;

  const { initialCurrentUser, offers } = useAppContext();
  const { newsletter } = useNewsletter(newsletterId);
  const { isSubscribed } = useCurrentNewsletterSubscription(
    newsletterId ?? 0,
    !!initialCurrentUser && !!newsletterId
  );
  const { trackEvent } = useEventTracking({
    contentType: ContentType.Newsletter,
    contentId: newsletterId,
  });

  const [subscribed, setSubscribed] = useState(false);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (isSubscribed !== undefined) {
      setSubscribed(isSubscribed);
    }
  }, [isSubscribed]);

  if (!newsletterId) {
    return null;
  }

  const title = config?.newsletter_name || content?.name || newsletter?.name || '';
  const description =
    config?.newsletter_description || content?.description || newsletter?.description || '';
  const ctaOverride = config?.newsletter_cta;
  const imageUrl = content?.image_url || newsletter?.cardImageUrlImgix;
  const showImage = Boolean(config?.is_newsletter_image_visible && imageUrl);

  const accessLevel = newsletter?.accessLevel ?? 0;
  const isSubscriberOnly = Boolean(newsletter?.isSubscriberOnly);
  const isPro = Boolean(newsletter?.isProOnly);
  const canSubscribeToNewsletter =
    (initialCurrentUser?.access_level ?? 0) >= accessLevel || !isSubscriberOnly;

  const toggleModal = () => setShowModal((prev) => !prev);

  const trackClick = (clickTarget: ClickTarget, label: string) => {
    trackEvent(click({ clickTarget, clickText: label }));
  };

  return (
    <div
      className={clsx(
        'flex flex-col gap-16 p-24',
        '[.is-carouseled_&]:min-w-[65svw] [.is-carouseled_&]:!border-0 [.is-carouseled_&]:px-0 [.is-carouseled_&]:!pb-0 [.is-carouseled_&]:sm:min-w-[calc(50svw-72px)] [.is-carouseled_&]:nav:min-w-0'
      )}
    >
      {showImage && (
        <div className="w-full sm:shrink-0">
          <LazyImgixImage src={imageUrl} wrapperClassName="!flex" className="w-full object-cover" />
        </div>
      )}
      <div className="flex flex-col">
        <h3 className="m-0 font-suisse-neue text-headline-xxs">{title}</h3>
        {description && (
          <p className="mb-0 mt-8 font-suisse-intl text-body-lg font-normal">{description}</p>
        )}
        <div className="mt-24">
          <NewsletterActionButton
            id={newsletterId}
            newsletterName={title}
            subscribeLabelOverride={ctaOverride}
            initialCurrentUser={initialCurrentUser as User}
            currentUserSubscribed={subscribed}
            canSubscribeToNewsletter={canSubscribeToNewsletter}
            isPro={isPro}
            offers={offers}
            isCurrentlySubscriber={initialCurrentUser?.is_subscriber}
            isProSubscriber={initialCurrentUser?.is_pro_subscriber}
            onClickCapture={trackClick}
            toggleModal={toggleModal}
            updateStateHandlerOnSubscribe={() => setSubscribed(true)}
            updateStateHandlerOnUnsubscribe={() => setSubscribed(false)}
          />
        </div>
      </div>

      {showModal && (
        <SignUpModal
          newsletterIds={[newsletterId]}
          description={description}
          headline={`Subscribe to ${title} Newsletter`}
          name={title}
          onClose={toggleModal}
        />
      )}
    </div>
  );
};

export default NewsletterBlock;
